import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/atoms/NewPcButton';
import SearchIcon from '@/assets/icons/Monitoring/SearchIcon.svg?react';
import { LayoutStickySection } from '../orders/_layouts/LayoutOrderContainer';
import { useToast } from '@/hooks/V3/useToast';
import { useAppSelector } from '@/redux/store';
import { useMonitoringGoods } from './MonitoringContext';
import { ROUTES } from '@/routes/paths';
import { useLog } from '@/hooks/V3/useLog';
import { useRecoilValue } from 'recoil';
import { deviceUuidState } from '@/store/atom';
import useExtensionInstalled from '@/hooks/useExtensionInstalled';
import useIsChromium from '@/hooks/useIsChromium';
import useCheckMembership from '@/hooks/useCheckMembership';
import ExtensionInstallDialog from './ExtensionInstallDialog';
import UnsupportedBrowserDialog from './UnsupportedBrowserDialog';
import MonitoringTutorialOverlay from './MonitoringTutorialOverlay';
import MonitoringLimitDialog from './MonitoringLimitDialog';

// import axios from 'axios';
// import { Dialog, DialogContent, DialogClose } from '@/components/atoms/Dialog';
// import { usePost, CPLAT_API_ROUTES } from '@/api';
// import { cplatApiV2 } from '@/api';
// import SubmitMonitoring from './SubmitMonitoring';

export const SearchBar = () => {
  const [url, setUrl] = useState('');
  // const [dialogOpen, setDialogOpen] = useState(false);
  // const [searchResult, setSearchResult] = useState<any>(null);
  // const [relatedList, setRelatedList] = useState<string[]>([]);
  // const [isSearching, setIsSearching] = useState(false);
  const [extensionDialogOpen, setExtensionDialogOpen] = useState(false);
  const [unsupportedBrowserDialogOpen, setUnsupportedBrowserDialogOpen] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [limitDialogOpen, setLimitDialogOpen] = useState(false);
  const isChromium = useIsChromium();
  const { showToast } = useToast();
  const user = useAppSelector((state) => state.user);
  const { goods, refetch, enabledMonitoringCount } = useMonitoringGoods();
  const { isExtensionInstalled } = useExtensionInstalled();
  const isMember = useCheckMembership();
  const fallbackMonitoringCount = isMember ? 10 : 1;
  const maxMonitoringCount = enabledMonitoringCount ?? fallbackMonitoringCount;
  const navigate = useNavigate();

  // // 등록 mutation
  // const { mutate: addMonitoringGoods, isPending: isAdding } = usePost(
  //   CPLAT_API_ROUTES.setUserMonitoringGoods,
  // );

  const { setUserEventHistory } = useLog(user.UserNo);
  const deviceUuid = useRecoilValue(deviceUuidState);

  // 확장프로그램 미설치 시 검색 영역 클릭 → 팝업
  const handleSearchAreaClick = () => {
    if (!isChromium) {
      setUnsupportedBrowserDialogOpen(true);
    } else if (!isExtensionInstalled) {
      setExtensionDialogOpen(true);
    } else if (goods.length >= maxMonitoringCount) {
      setLimitDialogOpen(true);
    }
  };

  // URL 유효성 검사 (brand.naver.com 또는 smartstore.naver.com)
  const isValidNaverUrl = (input: string): boolean => {
    try {
      const trimmed = input.trim();
      // 프로토콜이 없으면 붙여서 URL 파싱
      const urlStr = trimmed.startsWith('http') ? trimmed : `https://${trimmed}`;
      const parsed = new URL(urlStr);
      return (
        parsed.hostname === 'brand.naver.com' ||
        parsed.hostname === 'smartstore.naver.com' ||
        parsed.hostname.endsWith('.brand.naver.com') ||
        parsed.hostname.endsWith('.smartstore.naver.com')
      );
    } catch {
      return false;
    }
  };

  // 검색 버튼 클릭 핸들러 — 유효한 URL이면 새 탭으로 열기
  const handleSearch = () => {
    if (!isChromium) {
      setUnsupportedBrowserDialogOpen(true);
      return;
    }
    if (!isExtensionInstalled) {
      setExtensionDialogOpen(true);
      return;
    }

    if (goods.length >= maxMonitoringCount) {
      setLimitDialogOpen(true);
      return;
    }

    if (!url.trim()) {
      showToast('warning', 'URL을 입력해주세요.');
      return;
    }

    if (!isValidNaverUrl(url)) {
      showToast('warning', 'brand.naver.com 또는 smartstore.naver.com 상품 URL을 입력해주세요.');
      return;
    }

    setUserEventHistory({
      eventName: 'monitoring_product_search_web_dashboard',
      eventValue: url.trim(),
      deviceUuid,
    });

    const trimmed = url.trim();
    const targetUrl = trimmed.startsWith('http') ? trimmed : `https://${trimmed}`;

    // 새 탭으로 상품 페이지 열기 (유저 클릭 컨텍스트 → 팝업 차단 X)
    window.open(targetUrl, '_blank');

    // 튜토리얼 오버레이 표시
    setTutorialOpen(true);
  };

  // // 등록 버튼 클릭 핸들러 (react-query mutation)
  // const handleRegister = () => {
  //   if (!searchResult) return;
  //   addMonitoringGoods(
  //     {
  //       cplatToken: user.CplatToken,
  //       StoreName: searchResult.StoreName,
  //       GoodsNo: searchResult.GoodsNo,
  //       GoodsImageUrl: searchResult.GoodsImageUrl,
  //       GoodsName: searchResult.GoodsName,
  //       CategoryFullName: searchResult.CategoryFullName,
  //       UseYn: 'Y',
  //       RelatedKeywordList: relatedList,
  //       GoodsUrl: searchResult.GoodsUrl,
  //       GoodsPrice: searchResult.GoodsPrice,
  //       relatedKeywordRankInfo: searchResult.RelatedKeywordRankingList?.[0],
  //       ShippingFee: searchResult.ShippingFee,
  //       GoodsRegisterDate: searchResult.GoodsRegisterDate,
  //       NoticeYn: 'N',
  //       IsMyStoreGoods: searchResult.IsMyStoreGoods,
  //       MallSeq: searchResult.MallSeq,
  //       InsertYn: 'Y',
  //     },
  //     {
  //       onSuccess: () => {
  //         showToast('success', '모니터링 상품이 추가되었습니다!');
  //         setDialogOpen(false);
  //         setUrl('');
  //         setSearchResult(null);
  //         setRelatedList([]);
  //
  //         refetch(); // 모니터링 상품 목록 새로고침
  //       },
  //       onError: () => {
  //         showToast('warning', '상품 등록에 실패했습니다.');
  //       },
  //     },
  //   );
  // };
  // const MAX_KEYWORDS = 10;
  //
  // const handleToggleKeyword = (keyword: string) => {
  //   if (relatedList.includes(keyword)) {
  //     setRelatedList(relatedList.filter((k) => k !== keyword));
  //   } else if (relatedList.length < MAX_KEYWORDS) {
  //     setRelatedList([...relatedList, keyword]);
  //   } else {
  //     showToast('warning', `최대 ${MAX_KEYWORDS}개까지 선택할 수 있습니다.`);
  //   }
  // };

  return (
    <>
    <UnsupportedBrowserDialog
      open={unsupportedBrowserDialogOpen}
      onOpenChange={setUnsupportedBrowserDialogOpen}
    />
    <ExtensionInstallDialog
      open={extensionDialogOpen}
      onOpenChange={setExtensionDialogOpen}
    />
    <LayoutStickySection className="max-w-full border-none bg-gr-50/90 px-0 py-[10px]">
      <div
        className={`flex h-[54px] max-w-full rounded-[16px] border border-gr-300 bg-white px-[12px] py-[10px] focus-within:border-point-400 ${!isChromium || !isExtensionInstalled || goods.length >= maxMonitoringCount ? 'cursor-pointer' : ''}`}
        onClick={!isChromium || !isExtensionInstalled || goods.length >= maxMonitoringCount ? handleSearchAreaClick : undefined}
      >
        <input
          type="text"
          placeholder="모니터링할 상품의 Url을 입력해주세요"
          className="h-full flex-1 pr-[10px] text-gr-800 Body5M14 focus:outline-none"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          readOnly={!isChromium || !isExtensionInstalled || goods.length >= maxMonitoringCount}
          tabIndex={!isChromium || !isExtensionInstalled || goods.length >= maxMonitoringCount ? -1 : undefined}
        />
        <Button
          className="h-[34px] min-w-[67px] gap-[6px] px-[10px] py-[6px] Body5M14"
          onClick={handleSearch}
        >
          <SearchIcon />
          검색
        </Button>
      </div>
    </LayoutStickySection>
    <MonitoringLimitDialog
      open={limitDialogOpen}
      onOpenChange={setLimitDialogOpen}
      isMember={isMember}
      maxCount={maxMonitoringCount}
    />
    <MonitoringTutorialOverlay open={tutorialOpen} onClose={() => setTutorialOpen(false)} />
    {/* <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>

      <SubmitMonitoring
        isSearching={isSearching}
        handleSearch={handleSearch}
        searchResult={searchResult}
        setSearchResult={setSearchResult}
        setDialogOpen={setDialogOpen}
        setUrl={setUrl}
        url={url}
      />
    </Dialog> */}
    </>
  );
};
